import type { ExpenseAttachmentRow } from '../domain/expenseTypes';
import { supabase } from '../../shared/supabase/supabaseClient';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';

// ADR-017: full-online en todas las plataformas. No hay SQLite operativo ni
// cola de sync — los attachments se suben directo a Supabase Storage y se
// registran via RPC. El path anterior (db.runAsync + enqueue) era dead code
// del stub localDb y nunca persistía (issue #901).

function decodeBase64(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function attachmentExtension(mimeType: string): string {
  if (mimeType.startsWith('image/png')) return 'png';
  if (mimeType.startsWith('image/jpeg') || mimeType.startsWith('image/jpg')) return 'jpg';
  if (mimeType === 'application/pdf') return 'pdf';
  return 'bin';
}

export async function saveExpenseAttachmentLocal(params: {
  attachmentId: string;
  expenseId: string;
  branchId: string;
  orgId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  fileBase64: string;
  localUri?: string | null;
  createdBy?: string | null;
}): Promise<string> {
  const { attachmentId, expenseId, orgId, branchId, fileName, mimeType, fileSize, fileBase64, createdBy } = params;

  if (!attachmentId || !orgId || !expenseId || !fileBase64) {
    throw new Error('Faltan datos para adjuntar el comprobante.');
  }

  const createdAt = new Date().toISOString();
  const ext = attachmentExtension(mimeType);
  const storagePath = `${orgId}/${expenseId}/${attachmentId}.${ext}`;

  // 1. Subir el archivo al bucket privado expense-receipts (RLS scopea por orgId).
  try {
    const { error: uploadError } = await supabase.storage
      .from('expense-receipts')
      .upload(storagePath, decodeBase64(fileBase64), {
        contentType: mimeType,
        upsert: true,
      });
    if (uploadError) {
      throw new Error(`No pude subir el comprobante: ${uploadError.message}`);
    }
  } catch (err) {
    if (err instanceof Error && err.message.startsWith('No pude subir el comprobante')) throw err;
    const message = err instanceof Error ? err.message : String(err);
    captureOpsFailure({
      module: 'expenses',
      op: 'expenses.attachment.upload',
      error: err instanceof Error ? err : new Error(message),
      path: 'storage',
      itemId: expenseId,
      extra: { expenseId, attachmentId },
    });
    throw new Error(`No pude subir el comprobante: ${message}`);
  }

  // 2. Registrar el attachment en pos.expense_attachments via RPC.
  const { error: rpcError } = await supabase.schema('pos').rpc('apply_expense_attachment_command', {
    p_operation: 'insert',
    p_payload: {
      attachmentId,
      expenseId,
      branchId,
      orgId,
      fileName,
      fileUrl: storagePath,
      mimeType,
      fileSize,
      createdAt,
      createdBy: createdBy ?? '',
      operationId: `attach-${attachmentId}`,
    },
  });

  if (rpcError) {
    // El archivo quedó en storage pero el registro RPC falló — best-effort cleanup.
    try {
      await supabase.storage.from('expense-receipts').remove([storagePath]);
    } catch {
      // ignore cleanup failure
    }
    captureOpsFailure({
      module: 'expenses',
      op: 'expenses.attachment.rpc',
      error: new Error(rpcError.message),
      path: 'rpc',
      itemId: expenseId,
      extra: { expenseId, attachmentId },
    });
    throw new Error(`No pude registrar el comprobante: ${rpcError.message}`);
  }

  return attachmentId;
}

export async function listAttachmentsForExpense(expenseId: string): Promise<ExpenseAttachmentRow[]> {
  const { data, error } = await supabase
    .schema('pos')
    .from('expense_attachments')
    .select('id, expense_id, file_name, file_url, mime_type, file_size, created_at, created_by')
    .eq('expense_id', expenseId)
    .order('created_at', { ascending: true });

  if (error) {
    captureOpsFailure({
      module: 'expenses',
      op: 'expenses.attachment.list',
      error: new Error(error.message),
      path: 'select',
      itemId: expenseId,
    });
    return [];
  }

  return (data ?? []).map((row) => ({
    id: String(row.id),
    expenseId: String(row.expense_id),
    fileName: String(row.file_name),
    fileUrl: row.file_url != null ? String(row.file_url) : null,
    mimeType: String(row.mime_type ?? 'application/octet-stream'),
    fileSize: Number(row.file_size ?? 0),
    createdAt: String(row.created_at ?? ''),
    createdBy: row.created_by != null ? String(row.created_by) : null,
    syncStatus: 'synced' as const,
    localUri: null,
  }));
}

// No-op en full-online: el RPC insert ya persiste directamente. Se mantiene
// exportado porque flushRpc.ts lo importa (path nativo legacy).
export async function markLocalExpenseAttachmentSynced(_attachmentId: string, _fileUrl: string): Promise<void> {
  // ADR-017: sin SQLite local — nada que marcar.
}

export async function deleteExpenseAttachmentLocal(attachmentId: string): Promise<void> {
  // 1. Leer el row para obtener file_url (necesario para borrar de storage).
  const { data: row, error: selectError } = await supabase
    .schema('pos')
    .from('expense_attachments')
    .select('file_url')
    .eq('id', attachmentId)
    .maybeSingle();

  // 2. Borrar el registro via RPC (idempotente vía try_claim_device_command).
  const { error: rpcError } = await supabase.schema('pos').rpc('apply_expense_attachment_command', {
    p_operation: 'delete',
    p_payload: { attachmentId, operationId: `delete-${attachmentId}` },
  });

  if (rpcError) {
    throw new Error(`No pude borrar el comprobante: ${rpcError.message}`);
  }

  // 3. Best-effort: borrar el archivo de storage.
  if (!selectError && row?.file_url) {
    try {
      await supabase.storage.from('expense-receipts').remove([String(row.file_url)]);
    } catch {
      // best-effort: el registro ya se borró via RPC
    }
  }
}

// No-op en full-online: los attachments se leen directo de Supabase en
// listAttachmentsForExpense. Se mantiene exportado para compat del sync pull.
export async function upsertPulledExpenseAttachmentLocal(_raw: Record<string, unknown>): Promise<void> {
  // ADR-017: sin SQLite local — nada que upsertear.
}
